import { io, Socket } from 'socket.io-client';
import { authStore } from '../state/auth-store';

/**
 * Singleton Socket.io client. JWT is sent via the httpOnly `gamer_id` cookie
 * (withCredentials: true) — server extracts it from the handshake headers.
 *
 * Automatic reconnection with exponential backoff; if the server emits
 * `server:shutdown` we wait 3s then reconnect (lets the new container boot).
 *
 * Game code imports `socket`, subscribes to events, and emits actions. Do not
 * instantiate a second io() client.
 */

/**
 * Socket URL resolution (same priority chain as api.ts):
 *  1. window.__ENV__.API_URL  → direct connection to the backend origin
 *  2. VITE_SOCKET_URL         → build-time override
 *  3. window.location.origin  → same-origin (proxy mode)
 *
 * Path: when connecting through a reverse proxy (same origin) use '/ws'
 * (Caddy rewrites it). When connecting directly to the backend (API_URL
 * set) use the default '/socket.io' path.
 */
const SOCKET_URL = window.__ENV__?.API_URL ?? import.meta.env.VITE_SOCKET_URL ?? window.location.origin;
const SOCKET_PATH = window.__ENV__?.API_URL ? '/socket.io' : '/ws';

export const socket: Socket = io(SOCKET_URL, {
  path: SOCKET_PATH,
  autoConnect: false,
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 500,
  reconnectionDelayMax: 10_000,
  randomizationFactor: 0.5,
  withCredentials: true,
});

socket.on('connect', () => {
  console.debug('[socket] connected', socket.id);
});

socket.on('disconnect', (reason) => {
  console.debug('[socket] disconnected', reason);
});

socket.on('server:shutdown', (_data: { in: number; message: string }) => {
  console.warn('[socket] server restarting — reconnecting in 3s');
  socket.disconnect();
  setTimeout(() => socket.connect(), 3000);
});

socket.on('server:error', (err: { code: string; message: string }) => {
  console.error('[socket] server error', err);
  if (err.code === 'AUTH_FAILED') {
    authStore.set({ user: null, status: 'idle', error: null });
    socket.disconnect();
  }
});

export function connectSocket(): void {
  if (!socket.connected) socket.connect();
}

export function disconnectSocket(): void {
  socket.disconnect();
}
